import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import TokenPurchase from './TokenPurchase';
import TokenDisplay from './TokenDisplay';
import InsufficientTokensModal from './InsufficientTokensModal';
import { useTranslation } from 'react-i18next';
import { IoSend, IoTrashOutline, IoChatbubbleEllipsesOutline } from 'react-icons/io5';

export const checkTokenBalance = async () => {
  try {
    // Get the authentication provider and the appropriate token
    const authProvider = localStorage.getItem('auth_provider');
    let token;

    if (authProvider === 'email') {
      token = localStorage.getItem('email_token');
    } else {
      // Default to wechat token for backward compatibility
      token = localStorage.getItem('wechat_token');
    }

    // Use the access_token as fallback if specific token not found
    if (!token) {
      token = localStorage.getItem('access_token');
    }

    if (!token) {
      throw new Error('No authentication token found');
    }

    // Use the same API endpoint for both auth providers
    const response = await fetch('https://api.labcat.com.cn/api/tokens/balance', {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (response.ok) {
      const data = await response.json();
      return data.tokens_remaining;
    }
    throw new Error('Failed to fetch token balance');
  } catch (error) {
    console.error('Error checking token balance:', error);
    return 0;
  }
};

export const generateText = async (prompt, text, model, onContentReceived, onComplete, minimumTokens = 1000) => {
  const balance = await checkTokenBalance();

  if (balance < minimumTokens) {
    throw new Error('INSUFFICIENT_TOKENS');
  }

  const API_URL = 'https://api.labcat.com.cn/moonshot/generate_moonshot';
  const MAX_RETRIES = 3;
  let retries = 0;

  // Get the authentication provider and the appropriate token
  const authProvider = localStorage.getItem('auth_provider');
  let token;

  if (authProvider === 'email') {
    token = localStorage.getItem('email_token');
  } else {
    // Default to wechat token for backward compatibility
    token = localStorage.getItem('wechat_token');
  }

  // Use the access_token as fallback if specific token not found
  if (!token) {
    token = localStorage.getItem('access_token');
  }

  if (!token) {
    throw new Error('No authentication token found. Please login.');
  }

  // Token estimation
  const estimateTokens = (str) => str.split(/\s+/).length;
  const MAX_TOKENS = 15000;

  if (estimateTokens(prompt + text) > MAX_TOKENS) {
    console.warn("Warning: The combined prompt and text may exceed the token limit.");
    onContentReceived("Warning: The input text is too long and may exceed the token limit. The response might be incomplete.\n\n");
  }

  while (retries < MAX_RETRIES) {
    try {
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,  // Use token directly
        },
        body: JSON.stringify({
          prompt,
          text,
          model,
        }),
      });

      if (!response.ok) {
        // Handle specific error cases
        if (response.status === 401) {
          throw new Error('Authentication failed. Please login again.');
        }
        if (response.status === 402) {
          throw new Error('Insufficient tokens. Please purchase more tokens.');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      if (!response.body) {
        throw new Error('Failed to get a valid response body from the API');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });

        const lines = chunk.split('\n');
        for (const line of lines) {
          const trimmedLine = line.trim();
          if (trimmedLine.startsWith('data:') && trimmedLine !== 'data: [DONE]') {
            try {
              const jsonData = JSON.parse(trimmedLine.substring(5));
              if (jsonData.error) {
                // Handle specific error messages
                if (jsonData.error.includes('Insufficient tokens')) {
                  onContentReceived('\n\nError: Insufficient tokens. Please purchase more tokens.\n');
                  throw new Error('Insufficient tokens');
                }
                console.error('Error from server:', jsonData.error);
                throw new Error(jsonData.error);
              }
              jsonData.choices?.forEach((choice) => {
                if (choice.delta?.content) {
                  onContentReceived(choice.delta.content);
                }
              });
            } catch (error) {
              if (error.message === 'Insufficient tokens') {
                throw error; // Rethrow token errors to be handled by the caller
              }
              console.error('Error parsing JSON:', error);
            }
          }
        }
      }

      if (onComplete) {
        onComplete();
      }

      return;
    } catch (error) {
      console.error(`Error during text generation (attempt ${retries + 1}):`, error);

      // Don't retry certain errors
      if (
        error.message.includes('Authentication failed') ||
        error.message.includes('Insufficient tokens')
      ) {
        throw error;
      }

      retries++;
      if (retries >= MAX_RETRIES) {
        throw error;
      }
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
  }
};

const ChatUI = ({ fullPDFText, activePdfName, isDarkMode, isAuthenticated = true, onLogin }) => {
  const { t } = useTranslation();
  const [message, setMessage] = useState('');
  const [chatMessages, setChatMessages] = useState([]);
  const [isAIThinking, setIsAIThinking] = useState(false);
  const [isAIResponding, setIsAIResponding] = useState(false);
  const chatContainerRef = useRef(null);

  const [exampleQuestions] = useState([
    { display: "What is this article mainly about?", actual: "Como experto investigador de alto nivel, describe profesionalmente de qué trata este artículo. Enfócate en el campo profesional, sin necesidad de mencionar el apoyo financiero, los agradecimientos y las referencias." },
    { display: "PPT summary in Chinese", actual: "请你扮演一位经验丰富的学术告专家，基于这篇论文的内容，用中文设计一个简洁而全面的PPT大纲，总共3页左右。每页列出要点，确保涵盖论文的主要内容、方法和结论。不需要讲资金支持，致谢和参考文献。" },
    { display: "Evaluate statistical methods", actual: "As a statistics expert, please explain in detail the statistical methods used in this paper. Include but not limited to: the specific statistical techniques used, why these methods were chosen, how these methods were applied to the research data, and the advantages and disadvantages of these statistical methods." },
    { display: "What are the limitations?", actual: "En tant qu'expert en évaluation par les pairs rigoureux, veuillez analyser et identifier soigneusement les limites potentielles de cet article de recherche. Considérez tous les aspects tels que la conception de l'étude, la sélection des échantillons, la collecte des données et les méthodes d'analyse, et expliquez brièvement comment ces limites peuvent affecter les résultats et les conclusions de l'étude." },
  ]);

  const [showInsufficientWarning, setShowInsufficientWarning] = useState(false);
  const [currentBalance, setCurrentBalance] = useState(0);
  const [showTokenPurchase, setShowTokenPurchase] = useState(false);
  const [forceTokenUpdate, setForceTokenUpdate] = useState(0);
  const [showTokenDisplay, setShowTokenDisplay] = useState(false);
  const tokenDisplayTimeoutRef = useRef(null);

  const handleInputChange = (event) => {
    setMessage(event.target.value);
  };

  const handleExampleClick = (question) => {
    setMessage(question.display);
    handleSendMessage(question.actual, question.display);
  };

  const handleSendMessage = async (customMessage = null, displayMessage = null) => {
    const messageToSend = customMessage || message.trim();
    const messageToDisplay = displayMessage || messageToSend;

    if (messageToSend !== '') {
      try {
        // Check balance first
        const balance = await checkTokenBalance();
        setCurrentBalance(balance);

        const CHAT_MINIMUM_TOKENS = 9000;  // Higher minimum for chat

        if (balance < CHAT_MINIMUM_TOKENS) {
          setShowInsufficientWarning(true);
          return;
        }

        setChatMessages((prevMessages) => [
          ...prevMessages,
          { text: messageToDisplay, type: 'user' },
        ]);
        setMessage('');
        setIsAIThinking(true);

        try {
          const prompt = "You are a great scientist and researcher. Please respond to the following question or request based on the context (which is an academic journal paper) I provided, provide your response in the most appropriate language for the user:";
          const context = fullPDFText;
          const text = `${context}\n\nQuestion: ${messageToSend}`;

          // NOTE: The model parameter is currently ignored by the backend, which always uses 'GLM-4-Flash'
          // This is kept for consistency and to support future model selection features
          const model = 'GLM-4-Flash';

          let fullResponse = '';
          let responseStarted = false;
          await generateText(
            prompt,
            text,
            model,
            (content) => {
              if (!responseStarted) {
                responseStarted = true;
                setIsAIResponding(true);
                setIsAIThinking(false);
              }
              fullResponse += content;
              setChatMessages((prevMessages) => {
                const newMessages = [...prevMessages];
                if (
                  prevMessages.length &&
                  prevMessages[prevMessages.length - 1].type === 'response'
                ) {
                  newMessages[prevMessages.length - 1].text = fullResponse;
                } else {
                  newMessages.push({ text: fullResponse, type: 'response' });
                }
                return newMessages;
              });
            },
            () => setForceTokenUpdate(prev => prev + 1),
            CHAT_MINIMUM_TOKENS  // Pass the minimum tokens requirement
          );
          // Reset AI state after successful completion
          setIsAIThinking(false);
          setIsAIResponding(false);
        } catch (error) {
          console.error('Error generating response:', error);

          // Check if the error is due to insufficient tokens
          if (error.message === 'INSUFFICIENT_TOKENS' ||
              error.message.includes('Insufficient tokens')) {
            setShowInsufficientWarning(true);
          } else {
            setChatMessages((prevMessages) => [
              ...prevMessages,
              { text: 'Failed to process request.', type: 'error' },
            ]);
          }

          setIsAIThinking(false);
          setIsAIResponding(false);
        }
      } catch (error) {
        console.error('Error in handleSendMessage:', error);
        if (error.message === 'INSUFFICIENT_TOKENS' ||
            error.message.includes('Insufficient tokens')) {
          setShowInsufficientWarning(true);
        } else {
          setChatMessages((prevMessages) => [
            ...prevMessages,
            { text: 'Failed to process request.', type: 'error' },
          ]);
        }
      }
      setShowTokenDisplay(true); // Show token display when sending message

      // Clear any existing timeout
      if (tokenDisplayTimeoutRef.current) {
        clearTimeout(tokenDisplayTimeoutRef.current);
      }

      // Hide token display after 1 minute
      tokenDisplayTimeoutRef.current = setTimeout(() => {
        setShowTokenDisplay(false);
      }, 60000);
    }
  };

  // Scroll to the bottom of the chat when messages change
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatMessages]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (tokenDisplayTimeoutRef.current) {
        clearTimeout(tokenDisplayTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className={`flex flex-col h-full ${isDarkMode ? 'bg-[#0d1117]' : 'bg-white'}`}>
      {/* Header */}
      <div className={`px-4 border-b ${isDarkMode ? 'border-[#1b2028]' : 'border-gray-100'} h-11 flex items-center justify-between flex-shrink-0`}>
        <div className="flex items-center gap-2">
          <IoChatbubbleEllipsesOutline className={`text-sm ${isDarkMode ? 'text-sky-400' : 'text-blue-500'}`} />
          <h2 className={`text-[13px] font-semibold tracking-tight ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>{t('chat.askAboutPDF')}</h2>
        </div>
        {showTokenDisplay && (
          <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            <TokenDisplay forceUpdate={forceTokenUpdate} />
          </div>
        )}
      </div>

      {/* Chat Messages */}
      <ul className="flex-grow overflow-y-auto p-4 chat-scrollbar space-y-3" ref={chatContainerRef}>
        {activePdfName && (
          <li className={`mb-3 text-[11px] text-center py-1.5 px-3 rounded-full mx-auto w-fit ${
            isDarkMode ? 'bg-[#161b22] text-gray-500' : 'bg-gray-100 text-gray-400'
          }`}>
            {activePdfName}
          </li>
        )}
        {chatMessages.map((msg, index) => (
          <li key={index} className={`max-w-full flex gap-x-2 ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`rounded-2xl px-3.5 py-2.5 break-words max-w-[85%] ${
              msg.type === 'user'
                ? 'bg-gradient-to-br from-blue-600 to-blue-700 text-white shadow-sm shadow-blue-900/20'
                : msg.type === 'error'
                  ? (isDarkMode ? 'bg-red-900/30 text-red-300 border border-red-800/30' : 'bg-red-50 text-red-700 border border-red-200')
                  : (isDarkMode ? 'bg-[#161b22] text-gray-200 border border-[#21262d]' : 'bg-gray-50 text-gray-800 border border-gray-100')
            }`}>
              <ReactMarkdown className={`text-[13px] leading-relaxed prose-sm ${msg.type === 'user' ? 'prose-invert' : ''}`}>{msg.text}</ReactMarkdown>
            </div>
          </li>
        ))}
        {isAIThinking && !isAIResponding && (
          <li className="max-w-full flex gap-x-2">
            <div className={`rounded-2xl px-4 py-3 ${isDarkMode ? 'bg-[#161b22] border border-[#21262d]' : 'bg-gray-50 border border-gray-100'}`}>
              <div className="flex items-center gap-1">
                <span className={`thinking-dot ${isDarkMode ? 'bg-sky-400' : 'bg-blue-500'}`}></span>
                <span className={`thinking-dot ${isDarkMode ? 'bg-sky-400' : 'bg-blue-500'}`}></span>
                <span className={`thinking-dot ${isDarkMode ? 'bg-sky-400' : 'bg-blue-500'}`}></span>
              </div>
            </div>
          </li>
        )}
      </ul>

      {/* Input Area */}
      <div className={`p-3 border-t ${isDarkMode ? 'border-[#1b2028]' : 'border-gray-100'}`}>
        {/* Example Questions */}
        {chatMessages.length === 0 && (
          <div className="mb-3">
            <p className={`text-[11px] font-medium uppercase tracking-wider mb-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              {t('chat.exampleQuestions') || 'Suggestions'}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {exampleQuestions.map((question, index) => (
                <button
                  key={index}
                  onClick={() => handleExampleClick(question)}
                  className={`text-[12px] px-2.5 py-1.5 rounded-lg text-left transition-all duration-150 ${
                    isDarkMode
                      ? 'bg-[#161b22] text-gray-300 hover:bg-[#1c2129] border border-[#21262d] hover:border-[#30363d]'
                      : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-100 hover:border-gray-200'
                  }`}
                >
                  {question.display}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input Row */}
        <div className="flex gap-2">
          <input
            type="text"
            placeholder={t('chat.typeYourQuestion')}
            value={message}
            onChange={handleInputChange}
            className={`flex-grow px-3.5 py-2 text-[13px] rounded-xl transition-all duration-200 ${
              isDarkMode
                ? 'bg-[#161b22] text-gray-200 border border-[#21262d] placeholder-gray-500 focus:border-sky-500/50 focus:bg-[#1c2129]'
                : 'bg-gray-50 text-gray-800 border border-gray-200 placeholder-gray-400 focus:border-blue-300 focus:bg-white'
            } focus:outline-none focus:ring-1 ${isDarkMode ? 'focus:ring-sky-500/20' : 'focus:ring-blue-200'}`}
            onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
          />
          <button
            onClick={() => handleSendMessage()}
            className={`px-3.5 rounded-xl transition-all duration-200 flex items-center justify-center ${
              message.trim() === '' || isAIThinking || isAIResponding
                ? (isDarkMode ? 'bg-[#161b22] text-gray-600' : 'bg-gray-100 text-gray-300')
                : (isDarkMode
                    ? 'bg-sky-600 hover:bg-sky-500 text-white shadow-sm shadow-sky-900/30'
                    : 'bg-blue-600 hover:bg-blue-500 text-white shadow-sm shadow-blue-200')
            }`}
            disabled={isAIThinking || isAIResponding || message.trim() === ''}
          >
            <IoSend className="w-4 h-4" />
          </button>
        </div>

        {/* Clear Chat */}
        {chatMessages.length > 0 && (
          <button
            onClick={() => setChatMessages([])}
            className={`mt-2 text-[11px] flex items-center gap-1 transition-colors duration-150 ${isDarkMode ? 'text-gray-500 hover:text-gray-400' : 'text-gray-400 hover:text-gray-500'}`}
          >
            <IoTrashOutline className="w-3 h-3" />
            {t('chat.clearChat')}
          </button>
        )}
      </div>

      {showInsufficientWarning && (
        <InsufficientTokensModal
          onClose={() => setShowInsufficientWarning(false)}
          onPurchase={() => {
            setShowInsufficientWarning(false);
            setShowTokenPurchase(true);
          }}
          requiredTokens={'至少 9000 '}
          currentBalance={currentBalance}
          isDarkMode={isDarkMode}
          isAuthenticated={isAuthenticated}
          onLogin={onLogin}
        />
      )}

      {showTokenPurchase && (
        <TokenPurchase
          isDarkMode={isDarkMode}
          onClose={() => setShowTokenPurchase(false)}
        />
      )}
    </div>
  );
};

export default ChatUI;
